import { FC } from 'react';
import { css } from '@compiled/react';
import { useSearchParams } from 'react-router-dom';
import { Checkbox } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import Badge from '@atlaskit/badge';

import { useQueryParams } from 'src/hooks/useQueryParams';
import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { transformTaxonomyLabel } from 'src/pages/CatalogPage/utils/transformTaxonomyLabel';

interface FiltersProps {
  filter: string;
  number?: number;
  isLoadingNumber?: boolean;
  queryParamKey: string;
  onFilterInteraction?: () => void;
  isFirstFilter?: boolean;
  isMobile?: boolean;
  isAdobeCatalog?: boolean;
  catalogMapping?: { [label: string]: string };
}

const FILTERS_STRING_MAX_LENGTH = 25;

export const Filter: FC<FiltersProps> = ({
  filter,
  queryParamKey,
  number,
  onFilterInteraction,
  isLoadingNumber,
  isFirstFilter,
  isMobile,
  isAdobeCatalog = false,
  catalogMapping,
}) => {
  const [searchParams] = useSearchParams();
  const { setQueryParam, removeQueryParam } = useQueryParams();
  const { user } = useAtlassianUser();

  const isAtLeastOneFilterCheckedFromTaxonomy = !!searchParams.getAll(queryParamKey)[0];

  let isChecked = false;

  if (isAtLeastOneFilterCheckedFromTaxonomy) {
    const urlValues = searchParams.getAll(queryParamKey)[0].split(',');

    if (isAdobeCatalog && queryParamKey === 'catalog' && catalogMapping) {
      const mappedValue = catalogMapping[filter];

      isChecked = urlValues.includes(filter) || Boolean(mappedValue && urlValues.includes(mappedValue));
    } else {
      isChecked = urlValues.includes(filter);
    }
  }

  const handleFilterSelect = (): void => {
    const filterValue =
      isAdobeCatalog && queryParamKey === 'catalog' && catalogMapping ? catalogMapping[filter] || filter : filter;

    if (isChecked) {
      removeQueryParam(queryParamKey, filterValue);
    } else {
      setQueryParam(queryParamKey, filterValue);

      sendUIEvent(
        'catalogScreen',
        'filter',
        'clicked',
        undefined,
        {
          taxonomy: queryParamKey,
          value: filter,
        },
        user?.account_id,
      );
    }
    onFilterInteraction && onFilterInteraction();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.code === 'Space') {
      handleFilterSelect();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const labelLines = transformTaxonomyLabel(filter, FILTERS_STRING_MAX_LENGTH);

  return (
    <div
      aria-label={`${queryParamKey} filter checkbox`}
      css={[
        filtersContainerStyles,
        isFirstFilter && isMobile && css({ marginTop: `${token('space.100')} !important` }),
        labelLines.length > 1 && multiLineLabelStyles,
      ]}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <Checkbox
        isChecked={isChecked}
        onChange={handleFilterSelect}
        label={
          <div data-testid="filter-label" css={labelStyles}>
            {labelLines.map((line) => (
              <span css={labelLineStyles} key={line}>
                {line}
              </span>
            ))}
          </div>
        }
        data-testid={`${queryParamKey}-filter`}
        tabIndex={-1}
      />
      {!isLoadingNumber && !isAdobeCatalog && (
        <div data-testid="filter-badge" css={badgeContainerStyles}>
          <Badge>{number || 0}</Badge>
        </div>
      )}
    </div>
  );
};

const filtersContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
  padding: '4px 0 4px 0',

  label: {
    whiteSpace: 'nowrap',
  },

  [media.below.md]: {
    padding: 0,
    marginTop: token('space.300'),
    marginBottom: token('space.300'),

    '&:last-child': {
      marginBottom: 0,
    },
  },
});

const multiLineLabelStyles = css({
  'label > span': {
    marginTop: '14px',

    [media.below.md]: {
      marginTop: '3px',
    },

    [media.below.sm]: {
      marginTop: '14px',
    },
  },
});

const labelStyles = css({
  display: 'flex',
  flexDirection: 'column',

  [media.below.md]: {
    flexDirection: 'row',
  },

  [media.below.sm]: {
    flexDirection: 'column',
  },
});

const labelLineStyles = css({
  '&:first-of-type': {
    [media.below.md]: {
      marginRight: token('space.050'),
    },

    [media.below.sm]: {
      marginRight: 0,
    },
  },
});

const badgeContainerStyles = css({
  display: 'flex',
  flexWrap: 'nowrap',
  marginLeft: '6px',
  width: '50px',
});
